import React, { useState } from "react";
import { ThemeProvider } from "@mui/system";
import { Tooltip, IconButton, Box, Chip } from "@mui/material";
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import DriveFileMoveRoundedIcon from "@mui/icons-material/DriveFileMoveRounded";
import EditIcon from "@mui/icons-material/Edit";
import FlagRoundedIcon from "@mui/icons-material/FlagRounded";
import { useDispatch } from "react-redux";

import * as DefaultRoutes from "@Routes/DefaultRoutes";
import GenericRnDListing from "../../RnDMember/GenericRnDListing/GenericRnDListing";
import DelegationRequestPopup from "@Components/DelegationRequestPopup/DelegationRequestPopup";
import { GET_EXIT_REQUEST, MY_RD_EXIT_REQUESTS_EXPORT } from "@API/APIRoutes";
import { setData } from "@Redux/requestDataSlice";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DoDisturbIcon from "@mui/icons-material/DoDisturb";
import {
  stringCustomOperators,
  numberCustomOperators,
} from "@Utils/Validations/HelperFunctions/CustomFilters";
import { getStatusColor } from "../../../Utils/Validations/HelperFunctions/StyleFunction";
import {TagsContainer, TagItem} from "../../Admin/ListingItems/ListingItems";

// ===================== CONSTANTS =====================
const API_ENDPOINT_MY_RD_PENDING = "/my/rd";

const PAGE_TITLE = "Incoming Exit Requests";
const ERROR_MESSAGE = "Failed to fetch incoming exit requests. Trying again!";

const TOOLTIP_RESPOND = "Respond to Request";
const TOOLTIP_EDIT_RESPONSE = "Edit Impact Assessment";
const TOOLTIP_DELEGATE = "Send Delegation Request";
const TOOLTIP_VIEW_DETAILS = "View Request Details";

const COLUMN_HEADERS = {
  CREATED_BY: "Created By",
  INGREDIENT: "Ingredient",
  CATEGORY: "Category",
  CREATED_ON: "Created",
  DEADLINE: "Primary Date",
  POM_DATE: "Secondary Date",
  STATUS: "Status",
  RESPONDED: "Responded",
  SOURCE: "Source",
  ACTIONS: "Actions",
};

const DEFAULT_VALUE = "-";
const ICON_SIZE = "small";

const formatDate = (dateString) => {
  return dateString ? new Date(dateString).toLocaleDateString() : DEFAULT_VALUE;
};

const getNestedValue = (obj, path) => {
  const value = path.split(".").reduce((acc, part) => acc?.[part], obj);
  return value || DEFAULT_VALUE;
};

// ===================== MAIN COMPONENT =====================
function RDManagerIncomingExitRequestsListing() {
  const dispatch = useDispatch();

  const [delegationPopupOpen, setDelegationPopupOpen] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [selectedCategoryAssignmentId, setSelectedCategoryAssignmentId] =
    useState(null);

  const defaultColumns = [
    {
      field: "ingredient",
      headerName: COLUMN_HEADERS.INGREDIENT,
      flex: 1.2,
      type: "string",
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div>
          <div>{params.row.ingredient.name}</div>
          <div style={{ fontSize: "0.75rem", color: "gray" }}>
            CAS: {params.row.ingredient.casNumber}
          </div>
        </div>
      ),
      filterOperators: stringCustomOperators,
    },
    {
      field: "ingredientName",
      headerName: "Ingredient Name",
      flex: 1.2,
      minWidth: 130,
      type: "string",
      valueGetter: (params) => params.row.ingredient.name,
      filterOperators: stringCustomOperators,
      sortable: false,
    },
    {
      field: "ingredientCasNumber",
      headerName: "CAS Number",
      flex: 1,
      minWidth: 110,
      type: "string",
      valueGetter: (params) => params.row.ingredient.casNumber,
      filterOperators: stringCustomOperators,
      sortable: false,
    },
    {
      field: "userEmail",
      headerName: COLUMN_HEADERS.CREATED_BY,
      flex: 1.5,
      minWidth: 160,
      valueGetter: (params) => getNestedValue(params.row, "user.email"),
      type: "string",
      filterOperators: stringCustomOperators,
      sortable: false,
    },
    // {
    //   field: "countries",
    //   headerName: "Region",
    //   flex: 2,
    //   minWidth: 200,
    //   filterable: false,
    //   renderCell: (params) => {
    //     const rawValue = params.row["countries"];
    //     if (!rawValue || rawValue.length === 0) return "N/A";
    //     let items = [];
    //     if (typeof rawValue === "string") {
    //       items = rawValue.split("\u001F");
    //     } else if (Array.isArray(rawValue)) {
    //       const grouped = rawValue.reduce((acc, item) => {
    //         const region = item?.regionName || "Unknown";
    //         const country = item?.name;
    //         if (!country) return acc;
    //         if (!acc[region]) acc[region] = [];
    //         acc[region].push(country);
    //         return acc;
    //       }, {});
    //       items = Object.entries(grouped).map(
    //         ([region, countries]) => `${region} : ${countries.join(" - ")}`
    //       );
    //     } else {
    //       return "N/A";
    //     }
    //     return (
    //       <TagsContainer>
    //         {items.map((item, index) => (
    //           <TagItem key={index}>{item}</TagItem>
    //         ))}
    //       </TagsContainer>
    //     );
    //   },
    // },
    {
      field: "categoryName",
      headerName: COLUMN_HEADERS.CATEGORY,
      flex: 1.2,
      minWidth: 120,
      type: "string",
      filterOperators: stringCustomOperators,
      sortable: false,
    },
    {
      field: "source",
      headerName: COLUMN_HEADERS.SOURCE,
      flex: 1.2,
      minWidth: 120,
      renderCell: (params) => {
        const isEscalated = params.row.isEscalated === true;
        return (
          <Chip
            icon={isEscalated ? <FlagRoundedIcon /> : null}
            label={isEscalated ? "Escalated" : "Assigned"}
            color={isEscalated ? "warning" : "primary"}
            size="small"
            variant="outlined"
          />
        );
      },
      valueGetter: (params) =>
        params.row.isEscalated ? "Escalated" : "Assigned",
      type: "string",
      filterOperators: stringCustomOperators,
    },
    {
      field: "createdAt",
      headerName: COLUMN_HEADERS.CREATED_ON,
      flex: 1,
      minWidth: 120,
      valueGetter: (params) =>
        params.row.createdAt ? new Date(params.row.createdAt) : null,
      valueFormatter: (params) => formatDate(params.value),
      type: "date",
    },
    {
      field: "primaryDate",
      headerName: COLUMN_HEADERS.DEADLINE,
      flex: 1.2,
      minWidth: 120,
      valueGetter: (params) =>
        params.row.primaryDate ? new Date(params.row.primaryDate) : null,
      valueFormatter: (params) => formatDate(params.value),
      type: "date",
    },
    {
      field: "secondaryDate",
      headerName: COLUMN_HEADERS.POM_DATE,
      flex: 1.2,
      minWidth: 120,
      valueGetter: (params) =>
        params.row.secondaryDate ? new Date(params.row.secondaryDate) : null,
      valueFormatter: (params) => formatDate(params.value),
      type: "date",
    },
    {
      field: "status",
      headerName: COLUMN_HEADERS.STATUS,
      flex: 1.5,
      minWidth: 140,
      renderCell: (params) => (
        <Chip
          label={params.row.status?.replace(/_/g, " ")}
          color={getStatusColor(params.row.status)}
          size="small"
        />
      ),
      type: "string",
      filterOperators: stringCustomOperators,
    },
    {
      field: "hasResponded",
      headerName: COLUMN_HEADERS.RESPONDED,
      flex: 1,
      minWidth: 100,
      renderCell: (params) => <RespondedIndicator params={params} />,
      type: "boolean",
      sortable: false,
    },
    {
      field: "actions",
      headerName: COLUMN_HEADERS.ACTIONS,
      sortable: false,
      filterable: false,
      width: 120,
      minWidth: 120,
      maxWidth: 120,
      disableReorder: true,
    },
  ];

  const initialColumnVisibilityModel = {
    ingredientCasNumber: false,
    ingredientName: false,
    createdAt: false,
    secondaryDate: false,
    ulInternalDate: false,
  };

  const RespondedIndicator = ({ params }) => {
    const hasResponded = params.row.hasResponded === true;
    return (
      <ThemeProvider theme={params.api?.state?.theme}>
        <Box sx={styles.complianceCellContainer}>
          <Box
            sx={{
              ...styles.complianceIndicator,
              backgroundColor: hasResponded ? "success.main" : "error.main",
            }}
          >
            {hasResponded ? (
              <CheckCircleIcon fontSize={ICON_SIZE} color="white" />
            ) : (
              <DoDisturbIcon fontSize={ICON_SIZE} color="white" />
            )}
          </Box>
        </Box>
      </ThemeProvider>
    );
  };

  const handleRespondClick = (row, navigate) => {
    dispatch(setData(row));
    navigate(
      DefaultRoutes.rdManagerRequestResponseFromIncomingRequests
        .replace(":reqId", row.id)
        .replace(":categoryAssignmentId", row.categoryAssignmentId)
    );
  };

  const handleDelegateClick = (requestId, categoryAssignmentId) => {
    setDelegationPopupOpen(true);
    setSelectedRequestId(requestId);
    setSelectedCategoryAssignmentId(categoryAssignmentId);
  };

  const handleViewDetailsClick = (row, navigate) => {
    dispatch(setData(row));
    navigate(
      DefaultRoutes.rdManagerRequestInfoFromIncomingRequests
        .replace(":reqId", row.id)
        .replace(":categoryAssignmentId", row.categoryAssignmentId)
    );
  };

  const handleCloseDelegationPopup = () => {
    setDelegationPopupOpen(false);
    setSelectedRequestId(null);
  };

  const renderActions = (params, navigate, theme) => {
    const hasResponded = params.row.hasResponded === true;
    return (
      <ThemeProvider theme={theme}>
        <Tooltip
          title={hasResponded ? TOOLTIP_EDIT_RESPONSE : TOOLTIP_RESPOND}
        >
          <IconButton
            color={hasResponded ? "primary" : "success"}
            onClick={() => handleRespondClick(params.row, navigate)}
            size="small"
            sx={styles.iconButton}
          >
            {hasResponded ? <EditIcon /> : <PlayArrowRoundedIcon />}
          </IconButton>
        </Tooltip>
        <Tooltip title={TOOLTIP_DELEGATE}>
          <IconButton
            color="#9e9e9e"
            onClick={() =>
              handleDelegateClick(
                params.row.id,
                params.row.categoryAssignmentId
              )
            }
            size="small"
          >
            <DriveFileMoveRoundedIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={TOOLTIP_VIEW_DETAILS}>
          <IconButton
            color="info"
            onClick={() => handleViewDetailsClick(params.row, navigate)}
            size="small"
            sx={styles.iconButton}
          >
            <LightbulbOutlinedIcon />
          </IconButton>
        </Tooltip>
      </ThemeProvider>
    );
  };

  return (
    <>
      <GenericRnDListing
        title={PAGE_TITLE}
        PageIcon={<ReceiptLongIcon color="white" fontSize="large" />}
        iconColor="primary.main"
        apiRoute={`${GET_EXIT_REQUEST}${API_ENDPOINT_MY_RD_PENDING}`}
        exportApi={MY_RD_EXIT_REQUESTS_EXPORT}
        defaultColumns={defaultColumns}
        renderActions={renderActions}
        errorMessage={ERROR_MESSAGE}
        buttons={[]}
        getRowId={(row) => `${row.id}_${row.categoryAssignmentId}`}
        initialColumnVisibilityModel={initialColumnVisibilityModel}
        serverPagination={true}
      />

      {delegationPopupOpen && (
        <DelegationRequestPopup
          open={delegationPopupOpen}
          onClose={handleCloseDelegationPopup}
          requestId={selectedRequestId}
          categoryAssignmentId={selectedCategoryAssignmentId}
        />
      )}
    </>
  );
}

const styles = {
  statusCell: {
    color: "orange",
    fontStyle: "italic",
    display: "flex",
    alignItems: "center",
  },
  iconButton: {
    mr: 0,
  },
  complianceCellContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "100%",
  },
  complianceIndicator: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 32,
    height: 32,
    borderRadius: "50%",
    color: "white",
    transform: "translateX(-16px)",
  },
};

export default RDManagerIncomingExitRequestsListing;
